import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Invite, PaginatedResponse, Role, User } from '@/types/api';

export interface UsersParams {
  organization_id?: string | null;
  role?: Role | null;
  staff_only?: boolean;
  limit?: number;
  offset?: number;
}

export function useUsers(params: UsersParams = {}) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  return useQuery({
    queryKey: [
      'users',
      params.organization_id ?? null,
      params.role ?? null,
      params.staff_only ?? false,
      limit,
      offset,
    ],
    queryFn: async () =>
      (
        await api.get<PaginatedResponse<User>>('/users', {
          params: {
            organization_id: params.organization_id ?? undefined,
            role: params.role ?? undefined,
            staff_only: params.staff_only || undefined,
            limit,
            offset,
          },
        })
      ).data,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      role: Role;
      organization_id: string | null;
      full_name?: string;
    }) => {
      const { data } = await api.post<Invite>('/users/invites', vars);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export interface BulkInviteItem {
  email: string;
  role: Role;
  organization_id: string | null;
}

export interface BulkInviteResultItem {
  email: string;
  status: 'sent' | 'skipped' | 'failed';
  reason?: string;
  invite_id?: string;
}

export interface BulkInviteResponse {
  results: BulkInviteResultItem[];
  sent: number;
  skipped: number;
  failed: number;
}

export function useBulkInviteUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invites: BulkInviteItem[]) => {
      const { data } = await api.post<BulkInviteResponse>('/users/invites/bulk', {
        invites,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDisableUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/users/${id}/disable`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; role?: Role; full_name?: string; is_disabled?: boolean }) => {
      const { id, ...body } = vars;
      const { data } = await api.patch<User>(`/users/${id}`, body);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useMe() {
  return useQuery({
    queryKey: ['users', 'me'],
    queryFn: async () => (await api.get<User>('/users/me')).data,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { full_name?: string }) => {
      const { data } = await api.patch<User>('/users/me', vars);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'me'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { current_password: string; new_password: string }) => {
      await api.post('/users/me/password', vars);
    },
  });
}

export interface OOOUpdateVars {
  is_ooo: boolean;
  backup_user_id?: string | null;
  note?: string | null;
}

export function useUpdateOOO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: OOOUpdateVars) => {
      const { data } = await api.patch<User>('/users/me/ooo', vars);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'me'] });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useGenerateResetLink() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<{ reset_url: string; token: string; expires_at: string }>(
        `/users/${userId}/generate-reset-link`,
      );
      return data;
    },
  });
}

export function useResetUserMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.post(`/users/${userId}/reset-mfa`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useRemoveUserFromOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, orgId }: { userId: string; orgId: string }) => {
      await api.delete(`/users/${userId}/memberships/${orgId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

export interface InvitesParams {
  organization_id?: string | null;
}

/** Fetch pending (not yet accepted) invites for all roles. */
export function useInvites(params: InvitesParams = {}) {
  return useQuery({
    queryKey: ['invites', params.organization_id ?? null],
    queryFn: async () =>
      (
        await api.get<Invite[]>('/users/invites', {
          params: {
            organization_id: params.organization_id ?? undefined,
          },
        })
      ).data,
  });
}

/** Revoke (delete) a pending invite. */
export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      await api.delete(`/users/invites/${inviteId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });
}

/** Resend an invite by creating a new one for the same email + role + org. */
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      role: Role;
      organization_id: string | null;
    }) => {
      const { data } = await api.post<Invite>('/users/invites', vars);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites'] }),
  });
}
